import React from "react";
import { useNavigate, Link } from "react-router-dom";
import AppFooter from "../components/AppFooter";
import SEO from "../components/SEO";
import logo from "../assets/logo_basic.png";

import { createFAQSchema } from "../seo/schemas/faqSchema";
import { createBreadcrumbSchema } from "../seo/schemas/breadcrumbSchema";
import { financialRunwayFAQs } from "../data/faqData";

const SITE_URL = "https://www.reignitespirit.com";
const PAGE_PATH = "/financial-runway-calculator-faq";
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;

export default function FinancialRunwayCalculatorFAQ() {
  const navigate = useNavigate();

  const faqs = financialRunwayFAQs;
  
  const faqSchema = createFAQSchema({
    faqs,
    pageUrl: PAGE_URL,
    pageName:
      "Career Transition and Financial Runway Calculator FAQ",
    description:
      "Frequently asked questions about financial runway, career transitions, severance, gratuity, debt and inflation.",
  });

  const breadcrumbSchema = createBreadcrumbSchema({
    pageUrl: PAGE_URL,
    items: [
      {
        name: "Home",
        url: `${SITE_URL}/landing`,
      },
      {
        name: "Financial Runway Calculator",
        url: `${SITE_URL}/runway-calculator`,
      },
      {
        name: "Financial Runway Calculator FAQ",
        url: PAGE_URL,
      },
    ],
  });

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${PAGE_URL}#webpage`,
    url: PAGE_URL,
    name: "Career Transition and Financial Runway Calculator FAQ",
    description:
      "Answers about calculating financial runway during a career pivot, including debt, inflation, gratuity, severance and regional support.",

    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },

    about: {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/runway-calculator#software-application`,
      name: "ReigniteSpirit Financial Runway Calculator",
      url: `${SITE_URL}/runway-calculator`,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
    },

    breadcrumb: {
      "@id": `${PAGE_URL}#breadcrumb`,
    },

    mainEntity: {
      "@id": `${PAGE_URL}#faq`,
    },

    inLanguage: "en",
  };

  return (
    <div className="min-h-screen bg-[#11111f] text-white">
      <SEO
        title="Career Transition & Financial Runway Calculator FAQ"
        description="Find answers about calculating your financial runway during a career pivot or reskilling phase, including debt, inflation, gratuity, severance and supported countries."
        path={PAGE_PATH}
        image="/og-financial-runway.jpeg"
        type="website"
        author="ReigniteSpirit"
        keywords="financial runway calculator, career transition calculator, career pivot budget, GCC gratuity calculator, severance calculation, reskilling budget"
        robots="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        schema={[
          webPageSchema,
          faqSchema,
          breadcrumbSchema,
        ]}
      />

      <header className="border-b border-[#EB6AB5]/20 bg-[#11111f]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/landing")}
            className="flex items-center gap-3 cursor-pointer text-left"
            aria-label="Go to ReigniteSpirit home page"
          >
            <img
              src={logo}
              alt="ReigniteSpirit logo"
              className="w-10 h-10"
            />

            <div>
              <div className="text-[#EB6AB5] font-bold">
                REIGNITE.SPIRIT
              </div>

              <p className="text-xs text-[#EB6AB5]/70 tracking-widest uppercase">
                Rekindle Purpose
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() =>
              navigate("/register?plan=free_calculator")
            }
            className="px-5 py-2 rounded-full bg-[#EB6AB5] text-black font-bold hover:bg-[#f08cc5] transition-colors"
          >
            Use Free Calculator
          </button>
        </div>
      </header>

      <main>
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-gray-400"
          >
            <Link
              to="/landing"
              className="hover:text-[#EB6AB5]"
            >
              Home
            </Link>

            <span
              className="mx-2"
              aria-hidden="true"
            >
              /
            </span>

            <Link
              to="/runway-calculator"
              className="hover:text-[#EB6AB5]"
            >
              Financial Runway Calculator
            </Link>

            <span
              className="mx-2"
              aria-hidden="true"
            >
              /
            </span>

            <span aria-current="page">
              FAQ
            </span>
          </nav>
        </div>

        <section
          id="faq-section"
          className="py-14 bg-[#11111f] text-white"
          aria-labelledby="page-title"
        >
          <div className="max-w-4xl mx-auto px-4">
            <header className="text-center mb-10">
  <h1
    id="page-title"
    className="text-3xl md:text-5xl font-extrabold mb-6"
  >
    Career Transition &amp; Financial Runway Calculator FAQ
  </h1>

  <p className="text-gray-300 leading-relaxed max-w-3xl mx-auto">
    Learn how financial runway is estimated during a career
    transition, including the effects of monthly expenses,
    debt, inflation, gratuity, severance and the time remaining
    before a planned career change.
  </p>

  {/* ===============================================
      PRIVACY MESSAGE
  =============================================== */}

  <p
    className="
      mt-4
      max-w-3xl
      mx-auto
      text-sm
      md:text-base
      font-bold
      text-[#EB6AB5]
      leading-relaxed
    "
  >
    🔒 No information that is entered here is stored by us
    or can be viewed by anyone in our organisation.
  </p>
</header>
            <div className="mb-8 p-4 bg-[#1a1a2e] border border-[#EB6AB5]/20 rounded-xl">
              <p className="text-sm text-gray-300 m-0">
                Looking for broader career audit guidance? Read our{" "}
                <Link
                  to="/faq"
                  className="text-[#EB6AB5] underline hover:text-[#f08cc5]"
                >
                  Career Pivot Report FAQ
                </Link>
                .
              </p>
            </div>

            <section aria-labelledby="frequently-asked-questions">
              <h2
                id="frequently-asked-questions"
                className="sr-only"
              >
                Frequently asked questions
              </h2>

              {faqs.map((item, index) => (
                <article
                  key={item.id}
                  id={`faq-${item.id}`}
                  className={`faq-item scroll-mt-24 border-b border-gray-800 py-6 ${
                    index === faqs.length - 1
                      ? "bg-[#1a1a2e] px-4 rounded-lg mt-6 border border-gray-800"
                      : ""
                  }`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3
                    id={item.id}
                    className={`font-bold mb-3 scroll-mt-24 ${
                      index === faqs.length - 1
                        ? "text-lg text-gray-300"
                        : "text-xl text-[#EB6AB5]"
                    }`}
                    itemProp="name"
                  >
                    <a
                      href={`#${item.id}`}
                      className="hover:underline"
                      aria-label={`Direct link to: ${item.q}`}
                    >
                      {index + 1}. {item.q}
                    </a>
                  </h3>

                  <div
                    itemProp="acceptedAnswer"
                    itemScope
                    itemType="https://schema.org/Answer"
                  >
                    <p
                      className={`leading-relaxed ${
                        index === faqs.length - 1
                          ? "text-gray-400 text-sm italic"
                          : "text-gray-300"
                      }`}
                      itemProp="text"
                    >
                      {item.a}
                    </p>
                  </div>
                </article>
              ))}
            </section>

            <section
              className="mt-12 p-6 bg-[#1a1a2e] border border-[#EB6AB5]/20 rounded-xl"
              aria-labelledby="related-resources-heading"
            >
              <h2
                id="related-resources-heading"
                className="text-2xl font-bold text-[#EB6AB5] mb-4"
              >
                Related career planning resources
              </h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to="/runway-calculator"
                  className="block p-4 rounded-lg border border-gray-700 hover:border-[#EB6AB5] transition-colors"
                >
                  <h3 className="font-bold text-white mb-1">
                    Financial Runway Calculator
                  </h3>

                  <p className="text-sm text-gray-400">
                    Estimate how long your available financial resources
                    may support your career transition.
                  </p>
                </Link>

                <Link
                  to="/snapshot"
                  className="block p-4 rounded-lg border border-gray-700 hover:border-[#EB6AB5] transition-colors"
                >
                  <h3 className="font-bold text-white mb-1">
                    Snapshot Career Report
                  </h3>

                  <p className="text-sm text-gray-400">
                    Review career risks, opportunities and practical next
                    steps.
                  </p>
                </Link>

                <Link
                  to="/deep-audit"
                  className="block p-4 rounded-lg border border-gray-700 hover:border-[#EB6AB5] transition-colors"
                >
                  <h3 className="font-bold text-white mb-1">
                    Deep Career Audit
                  </h3>

                  <p className="text-sm text-gray-400">
                    Explore a more detailed career transition and skills
                    analysis.
                  </p>
                </Link>

                <Link
                  to="/blog"
                  className="block p-4 rounded-lg border border-gray-700 hover:border-[#EB6AB5] transition-colors"
                >
                  <h3 className="font-bold text-white mb-1">
                    Career Intelligence Blog
                  </h3>

                  <p className="text-sm text-gray-400">
                    Read practical articles about career pivots, AI,
                    financial runway and reskilling.
                  </p>
                </Link>
              </div>
            </section>
          </div>
        </section>
      </main>

      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}